// path: /src/components/layout/HeaderNew.tsx
'use client';

import React, { useState, useEffect } from 'react';

import { Home, Menu, Search, X, FileText, Scale, Shield, User, ChevronDown, LogOut } from 'lucide-react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

import { clearAdminSessionHint, hasAdminSessionHint, setAdminSessionHint } from '@/features/admin/lib/adminSessionHint';
import { useDeviceWidth } from '@/hooks/useDeviceWidth';

interface NavItem {
  name: string;
  href?: string;
  action?: 'search';
  icon: React.ElementType;
}

const HeaderNew: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hasWorkspaceAccess, setHasWorkspaceAccess] = useState(false);
  const [activeEmail, setActiveEmail] = useState<string | null>(null);
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const isMobVersion = useDeviceWidth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    const refreshAccess = () => {
      try {
        const raw = localStorage.getItem('recentProperties');
        const parsed = raw ? JSON.parse(raw) : [];
        const hasReports = Array.isArray(parsed) && parsed.length > 0;
        const email = localStorage.getItem('report_user_email') ?? '';
        setHasWorkspaceAccess(hasReports && Boolean(email));
        setActiveEmail(email ? email : null);
      } catch {
        setHasWorkspaceAccess(false);
        setActiveEmail(null);
      }
    };

    const refreshAdminAccess = () => {
      if (!hasAdminSessionHint()) {
        setHasAdminAccess(false);
        return;
      }

      void import('@/features/admin/api/adminApi')
        .then(module => module.adminMe())
        .then(res => {
          setHasAdminAccess(Boolean(res.authenticated));
          if (res.authenticated) {
            setAdminSessionHint();
          } else {
            clearAdminSessionHint();
          }
        })
        .catch(() => {
          setHasAdminAccess(false);
          clearAdminSessionHint();
        });
    };

    refreshAccess();
    refreshAdminAccess();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('storage', refreshAccess);
    window.addEventListener('storage', refreshAdminAccess);
    window.addEventListener('recent-properties:updated', refreshAccess);
    window.addEventListener('auth:changed', refreshAccess);
    window.addEventListener('admin:auth:changed', refreshAdminAccess);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('storage', refreshAccess);
      window.removeEventListener('storage', refreshAdminAccess);
      window.removeEventListener('recent-properties:updated', refreshAccess);
      window.removeEventListener('auth:changed', refreshAccess);
      window.removeEventListener('admin:auth:changed', refreshAdminAccess);
    };
  }, []);

  // Close the desktop user menu on route change.
  useEffect(() => {
    setUserMenuOpen(false);
  }, [location.pathname, location.search, location.hash]);

  // Close the desktop user menu on outside click / Escape.
  useEffect(() => {
    if (!userMenuOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setUserMenuOpen(false);
    };

    const onMouseDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null;
      if (!el) return;
      if (el.closest('[data-header-user-menu]')) return;
      setUserMenuOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [userMenuOpen]);

  const triggerSearchIntent = () => {
    if (typeof window === 'undefined') return;

    setMobileMenuOpen(false);

    if (location.pathname === '/') {
      // ми на головній → просто фокус
      window.dispatchEvent(new Event('search:intent'));
    } else {
      // ми не на головній → redirect + focus
      navigate('/?focus=search', { state: { searchIntent: true } });
    }
  };

  const goToHero = () => {
    setMobileMenuOpen(false);

    if (location.pathname === '/') {
      document.getElementById('hero')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#hero');
    }
  };

  const handleLogout = () => {
    if (typeof window === 'undefined') return;
    // Keep this aligned with AccountPricingPage.logout behavior.
    window.localStorage.removeItem('report_user_email');
    window.localStorage.removeItem('report_account_created_email');
    window.localStorage.removeItem('access_token');
    window.dispatchEvent(new Event('auth:changed'));
  };

  const navigation: NavItem[] = [
    { name: 'Main', icon: Home },
    { name: 'Search', action: 'search', icon: Search },
    { name: 'My Reports', href: '/reports', icon: FileText },
    ...(hasWorkspaceAccess ? [{ name: 'Compare', href: '/compare', icon: Scale }] : []),
    ...(hasWorkspaceAccess ? [{ name: 'Account', href: '/account', icon: User }] : []),
    ...(hasAdminAccess
      ? [
          {
            name: 'Admin',
            href: `/admin?returnTo=${encodeURIComponent(
              `${location.pathname}${location.search}${location.hash || ''}`,
            )}`,
            icon: Shield,
          },
        ]
      : []),
  ];

  // Desktop header should stay minimal. Account/Admin live in the user dropdown.
  const desktopNavigation = navigation.filter(item => item.name !== 'Account' && item.name !== 'Admin');
  const adminHref = hasAdminAccess
    ? `/admin?returnTo=${encodeURIComponent(
        `${location.pathname}${location.search}${location.hash || ''}`,
      )}`
    : null;

  return (
    <>
      <header
        className={`fixed top-0 right-0 left-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-cream-white/90 py-3 shadow-lg backdrop-blur-md' : 'bg-transparent py-5'
        }`}
      >
        <div className="relative mx-auto flex max-w-7xl items-center px-4">
          {/* LOGO */}
          <Link to="/" aria-label="ScoreNest home" className="flex items-center gap-3">
            <img src="/images/logo/Logo-alone.png" alt="" className="h-10 w-10 object-contain" />
            <img src="/images/logo/Logo-coffe-text.png" alt="" className="h-5 object-contain" />
          </Link>

          {/* DESKTOP NAV */}
          {!isMobVersion && (
            <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-8 font-bold md:flex">
              {desktopNavigation.map(item => {
                const Icon = item.icon;

                if (item.name === 'Main') {
                  return (
                    <button
                      key={item.name}
                      onClick={goToHero}
                      className="text-warm-espresso hover:text-coffee-bean flex items-center gap-1 text-[15px] font-bold transition-colors"
                    >
                      <Icon size={16} />
                      {item.name}
                    </button>
                  );
                }

                if (item.action === 'search') {
                  return (
                    <button
                      key={item.name}
                      onClick={triggerSearchIntent}
                      className="text-warm-espresso hover:text-coffee-bean flex items-center gap-1 text-[15px] font-bold transition-colors"
                    >
                      <Icon size={16} />
                      {item.name}
                    </button>
                  );
                }

                return (
                  <NavLink
                    key={item.name}
                    to={item.href!}
                    className={({ isActive }) =>
                      `flex items-center gap-1 text-[15px] font-bold transition-colors ${
                        isActive ? 'text-warm-espresso' : 'text-warm-espresso hover:text-coffee-bean'
                      }`
                    }
                  >
                    <Icon size={16} />
                    {item.name}
                  </NavLink>
                );
              })}
            </nav>
          )}

          {/* DESKTOP USER MENU */}
          {!isMobVersion && (activeEmail || hasAdminAccess) && (
            <div className="relative ml-auto hidden md:block" data-header-user-menu>
              <button
                type="button"
                onClick={() => setUserMenuOpen(v => !v)}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                className="border-neutral-200 bg-white/80 hover:bg-white flex items-center gap-2 rounded-full border px-3 py-1.5 text-[13px] font-semibold text-neutral-700 shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-300"
              >
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                <span className="max-w-[220px] truncate font-semibold">{activeEmail ? activeEmail : 'Menu'}</span>
                <ChevronDown
                  className={`h-4 w-4 text-neutral-500 transition ${userMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {userMenuOpen && (
                <div
                  role="menu"
                  aria-label="User menu"
                  className="absolute right-0 top-full z-50 mt-2 w-[280px] overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-xl"
                >
                  <div className="border-b border-neutral-100 px-4 py-3">
                    <div className="text-[11px] font-semibold tracking-wide text-neutral-500">SIGNED IN</div>
                    <div className="text-warm-espresso mt-0.5 truncate text-[15px] font-semibold">
                      {activeEmail ? activeEmail : 'Admin session'}
                    </div>
                  </div>

                  <div className="p-2">
                    {hasWorkspaceAccess && (
                      <NavLink
                        role="menuitem"
                        to="/account"
                        onClick={() => setUserMenuOpen(false)}
                        className="hover:bg-warm-sand/40 flex items-center gap-3 rounded-xl px-3 py-2 text-[15px] font-semibold text-neutral-800 transition"
                      >
                        <User className="h-4 w-4 text-neutral-500" />
                        Account
                      </NavLink>
                    )}

                    {adminHref && (
                      <NavLink
                        role="menuitem"
                        to={adminHref}
                        onClick={() => setUserMenuOpen(false)}
                        className="hover:bg-warm-sand/40 flex items-center gap-3 rounded-xl px-3 py-2 text-[15px] font-semibold text-neutral-800 transition"
                      >
                        <Shield className="h-4 w-4 text-neutral-500" />
                        Admin
                      </NavLink>
                    )}

                    {activeEmail && (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setUserMenuOpen(false);
                          handleLogout();
                        }}
                        className="hover:bg-warm-sand/40 flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-[15px] font-semibold text-neutral-800 transition"
                      >
                        <LogOut className="h-4 w-4 text-neutral-500" />
                        Log out
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* MOBILE BUTTON */}
          {isMobVersion && (
            <div className="ml-auto flex items-center gap-2">
              {activeEmail && (
                <div className="hidden items-center gap-1 rounded-full border border-neutral-200 bg-white/80 px-2 py-1 text-[10px] text-neutral-600 shadow-sm sm:flex">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  <span>Signed in</span>
                </div>
              )}
              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open navigation menu"
                aria-controls="mobile-navigation-menu"
                aria-expanded={mobileMenuOpen}
                className="bg-warm-sand/60 flex h-10 w-10 items-center justify-center rounded-xl"
              >
                <Menu className="text-coffee-bean h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ================= MOBILE OVERLAY ================= */}
      {mobileMenuOpen && isMobVersion && (
        <>
          <div
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
            className="from-espresso/40 via-coffee-bean/30 to-espresso/50 fixed inset-0 z-40 bg-gradient-to-b backdrop-blur-xl"
          />

          <div
            id="mobile-navigation-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
            className="from-cream-white/90 via-latte-cream/90 to-warm-sand/95 border-neutral-border fixed top-0 right-0 left-0 z-50 rounded-b-[28px] border-b bg-gradient-to-b shadow-2xl backdrop-blur-2xl"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-3">
                <img src="/images/logo/Logo-alone.png" alt="" className="h-8 w-8" />
                <span className="text-warm-espresso font-serif text-lg">ScoreNest</span>
              </div>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close navigation menu"
                className="bg-warm-sand/70 flex h-9 w-9 items-center justify-center rounded-full"
              >
                <X className="text-coffee-bean h-5 w-5" />
              </button>
            </div>

            <nav className="space-y-2 px-4 pb-6">
              {navigation.map(item => {
                const Icon = item.icon;

                if (item.name === 'Main') {
                  return (
                    <button
                      key={item.name}
                      onClick={goToHero}
                      className="text-coffee-bean hover:bg-warm-sand/40 flex w-full items-center gap-3 rounded-xl px-4 py-3 font-bold transition"
                    >
                      <Icon size={18} />
                      {item.name}
                    </button>
                  );
                }

                if (item.action === 'search') {
                  return (
                    <button
                      key={item.name}
                      onClick={() => {
                        triggerSearchIntent();
                        setMobileMenuOpen(false);
                      }}
                      className="text-coffee-bean hover:bg-warm-sand/40 flex w-full items-center gap-3 rounded-xl px-4 py-3 font-bold transition"
                    >
                      <Icon size={18} />
                      {item.name}
                    </button>
                  );
                }

                return (
                  <NavLink
                    key={item.name}
                    to={item.href!}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-coffee-bean hover:bg-warm-sand/40 flex items-center gap-3 rounded-xl px-4 py-3 font-bold transition"
                  >
                    <Icon size={18} />
                    {item.name}
                  </NavLink>
                );
              })}
            </nav>
          </div>
        </>
      )}
    </>
  );
};

export default HeaderNew;
